import { useMemo, useState } from "react";

import { Button, Link, SegmentedControl, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { usePriceConfig } from "../../hooks/usePriceConfig";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import { SearchParam, SearchParamIntent, SubscriptionPeriod, SubscriptionTier } from "../../types";
import { getTaxLabel } from "../../utils/currencyUtils";

import style from "./PurchaseNotice.module.css";

interface BuySubscriptionProps {
  selectedTier: SubscriptionTier;
  selectedPeriod: SubscriptionPeriod;
  couponCode?: string;
  isTrial?: boolean;
}

export const PurchaseNotice: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(SubscriptionPeriod.Yearly);
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const tracker = useTracker();
  const { data: accountData } = useAccount();
  const { data: priceConfigData, isLoading: isPriceConfigLoading } = usePriceConfig();
  const { teamID } = useParams<{ teamID: string }>();

  const teamMembersReturnUrl = useMemo(() => {
    if (!teamID) {
      return undefined;
    }

    const url = new URL(`/teams/${teamID}/members`, window.location.origin);
    url.searchParams.set(SearchParam.Intent, SearchParamIntent.Invite);
    return url.toString();
  }, [teamID]);

  const buySubscription = ({
    selectedTier,
    selectedPeriod,
    couponCode,
    isTrial,
  }: BuySubscriptionProps) => {
    isTrial
      ? tracker.trackButtonClick({ action: ElementClickAction.PURCHASE_NOTICE_START_TRIAL })
      : tracker.trackButtonClick({ action: ElementClickAction.PURCHASE_NOTICE_SUBSCRIBE_NOW });
    navigation.goToCheckout({
      selectedTier,
      period: selectedPeriod,
      couponCode,
      returnToURL: teamMembersReturnUrl,
    });
  };

  if (!accountData || !priceConfigData || isPriceConfigLoading) {
    return <SpinnerCenter />;
  }

  const ProFeatures = [
    t(m.Purchase.Plans.Pro.Feature01),
    t(m.Purchase.Plans.Pro.Feature02),
    t(m.Purchase.Plans.Pro.Feature03),
    t(m.Purchase.Plans.Pro.Feature04),
    t(m.Purchase.Plans.Pro.Feature05),
    t(m.Purchase.Plans.Pro.Feature06),
    t(m.Purchase.Plans.Pro.Feature07),
  ];

  const isTrial =
    accountData?.availablePlans.some((plan) => plan.trialDays > 0) ||
    accountData?.techTrialAvailable;

  const taxLabel = getTaxLabel(priceConfigData?.currencySymbol);

  const originalPrice =
    selectedPeriod === SubscriptionPeriod.Yearly
      ? Math.ceil(priceConfigData.yearly / 12)
      : priceConfigData.monthly;
  const yearlyPriceBreakdown = `${priceConfigData?.currencySymbol}${
    priceConfigData.yearly
  }${"/seat "}${t(m.Purchase.PriceBilledAnnually, { taxLabel })}`;

  return (
    <div>
      <div className={style.title}>{isTrial ? "Try Pro" : "Subscribe to Pro"}</div>
      <SegmentedControl
        className={style.segmentedControl}
        defaultChecked={selectedPeriod === SubscriptionPeriod.Monthly}
        onChange={(e) => {
          setSelectedPeriod(
            e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly
          );
          tracker.trackButtonClick({
            action: ElementClickAction.SUBSCRIPTION_PERIOD_PICK,
            details: e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly,
          });
        }}
        optionA={t(m.Button.Yearly)}
        optionB={t(m.Button.Monthly)}
        optionASecondaryText={`- ${t(m.Purchase.PriceMonthlySave, {
          savingsPercent: priceConfigData.savingsPercent.toFixed(0),
        })}`}
      />
      <p
        className={style.monthlyPrice}
      >{`${priceConfigData?.currencySymbol}${originalPrice}${"/seat/month"}`}</p>
      <p className={style.yearlyPrice}>
        {selectedPeriod === SubscriptionPeriod.Yearly
          ? yearlyPriceBreakdown
          : t(m.Purchase.PriceBilledMonthly, { taxLabel })}
      </p>
      <div className={style.emphasized}>{t(m.Purchase.Plans.Pro.FeatureDescription)}</div>
      <ul className={style.featureList}>
        {ProFeatures.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      <div className={style.buttonContainer}>
        <Button
          onClick={() =>
            buySubscription({
              selectedPeriod,
              selectedTier: SubscriptionTier.Lite,
              isTrial,
            })
          }
        >
          {isTrial ? "Start 14-day free trial" : "Subscribe now"}
        </Button>
        <Link
          href="https://shapr3d.com/pricing"
          target="_blank"
          rel="noopener,noreferrer"
          onClick={() => {
            tracker.trackButtonClick({
              action: ElementClickAction.PURCHASE_NOTICE_COMPARE_SUBSCRIPTIONS,
            });
          }}
        >
          Compare subscriptions
        </Link>
      </div>
    </div>
  );
};
